import { useEffect, useState, useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import axios from "axios";
import {
    ArrowLeft,
    CheckCircle2,
    Circle,
    Clock,
    ExternalLink,
    Loader2,
    Send,
    FileText,
    Plus,
    Upload,
} from "lucide-react";
import { toast } from "sonner";
import { useClientAuth } from "@/lib/clientAuth";
import { findGrowthService } from "@/lib/growthServices";
import PortalShell from "@/components/PortalShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MILESTONE_ICON = {
    pending: Circle,
    in_progress: Clock,
    review: Clock,
    approved: CheckCircle2,
    completed: CheckCircle2,
};

const MILESTONE_COLOR = {
    pending: "text-bone-500",
    in_progress: "text-amber-400",
    review: "text-ember",
    approved: "text-emerald-400",
    completed: "text-emerald-400",
};

export default function ClientProjectDetail() {
    const { id } = useParams();
    const { client, token, loading, logout } = useClientAuth();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);
    const [comment, setComment] = useState("");
    const [posting, setPosting] = useState(false);

    const headers = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token],
    );

    const refresh = () =>
        axios
            .get(`${API}/client/projects/${id}`, { headers })
            .then((r) => setData(r.data))
            .catch((err) => {
                if (err?.response?.status === 401) logout();
                else toast.error("Could not load project");
            });

    useEffect(() => {
        if (!token) return;
        setBusy(true);
        refresh().finally(() => setBusy(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, id]);

    if (loading || busy)
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-ember" />
            </div>
        );
    if (!client) return <Navigate to="/portal/login" replace />;
    if (!data) return <Navigate to="/portal" replace />;

    const { project, milestones, resources, comments, scripts = [] } = data;
    const BACKEND = process.env.REACT_APP_BACKEND_URL;

    const onApprove = async (mid) => {
        try {
            await axios.post(
                `${API}/client/milestones/${mid}/approve`,
                {},
                { headers },
            );
            toast.success("Milestone approved");
            refresh();
        } catch (err) {
            toast.error("Could not approve");
        }
    };

    const onPostComment = async (e) => {
        e.preventDefault();
        if (!comment.trim()) return;
        setPosting(true);
        try {
            await axios.post(
                `${API}/client/projects/${id}/comments`,
                { body: comment.trim() },
                { headers },
            );
            setComment("");
            refresh();
        } catch (err) {
            toast.error("Could not post comment");
        } finally {
            setPosting(false);
        }
    };

    return (
        <PortalShell>
            <Link
                to="/portal"
                data-testid="portal-back"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-bone-300 hover:text-ember transition-colors mb-8"
            >
                <ArrowLeft className="w-3.5 h-3.5" />
                All projects
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                <div className="lg:col-span-8">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-ember mb-3">
                        {project.status.replace("_", " ")}
                    </div>
                    <h1
                        data-testid="project-title"
                        className="font-display text-bone text-4xl lg:text-5xl tracking-tightest leading-[1] mb-4"
                    >
                        {project.title}
                    </h1>
                    {project.summary && (
                        <p className="text-bone-300 text-lg leading-relaxed mb-10 max-w-[60ch]">
                            {project.summary}
                        </p>
                    )}

                    {/* Milestones */}
                    <section className="mb-12">
                        <h2 className="font-display text-bone text-xl tracking-tight mb-6 pb-3 border-b border-white/10">
                            Milestones
                        </h2>
                        {milestones.length === 0 ? (
                            <p className="text-bone-500 text-sm py-6">
                                Milestones will appear here as your studio lead
                                adds them.
                            </p>
                        ) : (
                            <ol className="space-y-3">
                                {milestones.map((m, i) => {
                                    const Icon = MILESTONE_ICON[m.status] || Circle;
                                    return (
                                        <li
                                            key={m.id}
                                            data-testid={`milestone-${m.id}`}
                                            className="border border-white/10 bg-ink-800/40 p-5 flex items-start gap-4"
                                        >
                                            <Icon
                                                className={`w-5 h-5 mt-1 shrink-0 ${MILESTONE_COLOR[m.status] || "text-bone-500"}`}
                                            />
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                                    <h3 className="font-display text-bone text-lg tracking-tight">
                                                        {String(i + 1).padStart(
                                                            2,
                                                            "0",
                                                        )}
                                                        . {m.title}
                                                    </h3>
                                                    <span className="text-[10px] uppercase tracking-[0.2em] text-bone-500">
                                                        {m.status.replace(
                                                            "_",
                                                            " ",
                                                        )}
                                                        {m.due_at &&
                                                            ` · due ${m.due_at.slice(0, 10)}`}
                                                    </span>
                                                </div>
                                                {m.description && (
                                                    <p className="text-bone-300 text-sm mt-2 leading-relaxed whitespace-pre-wrap">
                                                        {m.description}
                                                    </p>
                                                )}
                                                {m.requires_approval &&
                                                    m.status === "review" &&
                                                    !m.approved_by_client && (
                                                        <button
                                                            onClick={() =>
                                                                onApprove(m.id)
                                                            }
                                                            data-testid={`approve-${m.id}`}
                                                            className="mt-3 btn-ember inline-flex items-center gap-2 h-9 px-4 text-xs uppercase tracking-[0.2em]"
                                                        >
                                                            Approve
                                                        </button>
                                                    )}
                                                {m.approved_by_client && (
                                                    <span className="mt-2 inline-flex items-center gap-1.5 text-emerald-400 text-xs">
                                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                                        Approved
                                                    </span>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ol>
                        )}
                    </section>

                    {/* Scripts */}
                    {scripts.length > 0 && (
                        <section className="mb-12" data-testid="client-scripts">
                            <h2 className="font-display text-bone text-xl tracking-tight mb-6 pb-3 border-b border-white/10 flex items-center gap-2">
                                <FileText className="w-5 h-5 text-ember" />
                                Scripts
                            </h2>
                            <ul className="space-y-8">
                                {scripts.map((s) => (
                                    <ScriptItem
                                        key={s.id}
                                        script={s}
                                        token={token}
                                        backend={BACKEND}
                                    />
                                ))}
                            </ul>
                        </section>
                    )}

                    {/* Comments */}
                    <section>
                        <h2 className="font-display text-bone text-xl tracking-tight mb-6 pb-3 border-b border-white/10">
                            Notes
                        </h2>
                        <form
                            onSubmit={onPostComment}
                            data-testid="comment-form"
                            className="flex items-start gap-3 mb-6 border border-white/10 bg-ink-800/40 p-4"
                        >
                            <textarea
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Leave a note for the studio team…"
                                data-testid="comment-input"
                                rows={2}
                                className="flex-1 bg-transparent outline-none text-sm text-bone placeholder:text-bone-500 resize-none"
                            />
                            <button
                                type="submit"
                                disabled={posting || !comment.trim()}
                                data-testid="comment-submit"
                                className="btn-ember inline-flex items-center gap-2 h-10 px-4 text-xs uppercase tracking-[0.2em] disabled:opacity-50"
                            >
                                {posting ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                    <Send className="w-3.5 h-3.5" />
                                )}
                                Send
                            </button>
                        </form>
                        {comments.length === 0 ? (
                            <p className="text-bone-500 text-sm">
                                No notes yet.
                            </p>
                        ) : (
                            <ul className="space-y-4">
                                {comments
                                    .slice()
                                    .reverse()
                                    .map((c) => (
                                        <li
                                            key={c.id}
                                            className={`p-4 border ${
                                                c.author_type === "admin"
                                                    ? "border-ember/30 bg-ember/5"
                                                    : "border-white/10 bg-ink-800/40"
                                            }`}
                                        >
                                            <div className="flex items-center justify-between text-xs mb-2">
                                                <span className="font-display text-bone tracking-tight">
                                                    {c.author_name}
                                                    {c.author_type === "admin" && (
                                                        <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-ember">
                                                            Studio
                                                        </span>
                                                    )}
                                                </span>
                                                <span className="text-bone-500">
                                                    {new Date(
                                                        c.created_at,
                                                    ).toLocaleString()}
                                                </span>
                                            </div>
                                            <p className="text-bone-300 text-sm whitespace-pre-wrap leading-relaxed">
                                                {c.body}
                                            </p>
                                        </li>
                                    ))}
                            </ul>
                        )}
                    </section>
                </div>

                {/* Sidebar */}
                <aside className="lg:col-span-4 space-y-6">
                    <div className="border border-white/10 bg-ink-800/40 p-6">
                        <h3 className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-4">
                            Project details
                        </h3>
                        <dl className="space-y-3 text-sm">
                            {project.services?.length > 0 && (
                                <div>
                                    <dt className="text-bone-500 text-xs mb-2">
                                        Growth services
                                    </dt>
                                    <dd
                                        data-testid="client-project-services"
                                        className="flex flex-wrap gap-1.5"
                                    >
                                        {project.services.map((raw) => {
                                            const svc = findGrowthService(raw);
                                            return (
                                                <span
                                                    key={svc.id}
                                                    data-testid={`client-project-service-${svc.id}`}
                                                    title={svc.description || ""}
                                                    className="inline-flex items-center px-2.5 py-1 border border-ember/30 bg-ember/5 text-ember text-[10px] uppercase tracking-[0.18em]"
                                                >
                                                    {svc.label}
                                                </span>
                                            );
                                        })}
                                    </dd>
                                </div>
                            )}
                            {project.started_at && (
                                <div>
                                    <dt className="text-bone-500 text-xs">
                                        Started
                                    </dt>
                                    <dd className="text-bone">
                                        {project.started_at.slice(0, 10)}
                                    </dd>
                                </div>
                            )}
                            {project.target_at && (
                                <div>
                                    <dt className="text-bone-500 text-xs">
                                        Target
                                    </dt>
                                    <dd className="text-bone">
                                        {project.target_at.slice(0, 10)}
                                    </dd>
                                </div>
                            )}
                        </dl>
                    </div>

                    <div className="border border-white/10 bg-ink-800/40 p-6">
                        <h3 className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-4">
                            Resources
                        </h3>
                        {resources.length === 0 ? (
                            <p className="text-bone-500 text-xs">
                                Drive / Dropbox / Figma links your team shares
                                will appear here.
                            </p>
                        ) : (
                            <ul className="space-y-3">
                                {resources.map((r) => (
                                    <li key={r.id}>
                                        <a
                                            href={r.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`resource-${r.id}`}
                                            className="group flex items-center justify-between gap-3 text-sm text-bone hover:text-ember transition-colors"
                                        >
                                            <span className="truncate">
                                                {r.label}
                                            </span>
                                            <ExternalLink className="w-3.5 h-3.5 shrink-0 text-bone-500 group-hover:text-ember" />
                                        </a>
                                        <span className="text-[10px] uppercase tracking-[0.18em] text-bone-500">
                                            {r.kind}{" "}
                                            {r.added_by === "client" && "· you"}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <ClientSubmitForm
                        projectId={id}
                        headers={headers}
                        onAdded={refresh}
                    />
                </aside>
            </div>
        </PortalShell>
    );
}

function ClientSubmitForm({ projectId, headers, onAdded }) {
    const [label, setLabel] = useState("");
    const [url, setUrl] = useState("");
    const [kind, setKind] = useState("drive");
    const [busy, setBusy] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!label.trim() || !url.trim()) {
            toast.error("Add a label and a URL");
            return;
        }
        setBusy(true);
        try {
            await axios.post(
                `${API}/client/projects/${projectId}/resources`,
                { label: label.trim(), url: url.trim(), kind },
                { headers },
            );
            toast.success("Sent — your studio team will see it shortly");
            setLabel("");
            setUrl("");
            setKind("drive");
            onAdded?.();
        } catch {
            toast.error("Could not send link");
        } finally {
            setBusy(false);
        }
    };

    return (
        <form
            onSubmit={onSubmit}
            data-testid="client-submit-form"
            className="border border-dashed border-white/15 bg-ink-800/20 p-6"
        >
            <h3 className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2 flex items-center gap-2">
                <Upload className="w-3 h-3 text-ember" />
                Send a link
            </h3>
            <p className="text-bone-500 text-xs mb-4 leading-relaxed">
                For images, videos or large files — share a Google Drive,
                Dropbox or WeTransfer link.
            </p>
            <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Label (e.g. Reference video)"
                data-testid="client-submit-label"
                className="w-full bg-transparent outline-none border-b border-white/15 focus:border-ember py-2 text-bone text-sm mb-3"
            />
            <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://drive.google.com/…"
                data-testid="client-submit-url"
                className="w-full bg-transparent outline-none border-b border-white/15 focus:border-ember py-2 text-bone text-sm mb-3"
            />
            <select
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                data-testid="client-submit-kind"
                className="w-full bg-ink border border-white/10 focus:border-ember outline-none px-3 py-2 text-bone text-xs uppercase tracking-[0.18em] mb-4"
            >
                {["drive", "dropbox", "wetransfer", "figma", "loom", "notion", "link"].map(
                    (k) => (
                        <option key={k} value={k}>
                            {k}
                        </option>
                    ),
                )}
            </select>
            <button
                type="submit"
                disabled={busy}
                data-testid="client-submit-button"
                className="btn-ember w-full inline-flex items-center justify-center gap-2 h-10 text-xs uppercase tracking-[0.2em] disabled:opacity-60"
            >
                {busy ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                    <>
                        <Plus className="w-3.5 h-3.5" />
                        Submit link
                    </>
                )}
            </button>
        </form>
    );
}

function ScriptItem({ script, token, backend }) {
    const [comments, setComments] = useState([]);
    const [draft, setDraft] = useState("");
    const [posting, setPosting] = useState(false);
    const [showViewer, setShowViewer] = useState(true);
    const [fileUrl, setFileUrl] = useState(null);
    const isPdf = script.content_type === "application/pdf";

    const loadComments = () =>
        axios
            .get(`${backend}/api/scripts/${script.id}/comments`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((r) => setComments(r.data || []))
            .catch(() => {});

    useEffect(() => {
        loadComments();
        // Fetch the file as a Blob so we can show it inline (auth required for streaming)
        if (isPdf) {
            axios
                .get(`${backend}/api/client/scripts/${script.id}/file`, {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: "blob",
                })
                .then((r) => {
                    const url = URL.createObjectURL(r.data);
                    setFileUrl(url);
                })
                .catch(() => {});
        }
        return () => {
            if (fileUrl) URL.revokeObjectURL(fileUrl);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [script.id]);

    const downloadFile = async () => {
        try {
            const r = await axios.get(
                `${backend}/api/client/scripts/${script.id}/file`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: "blob",
                },
            );
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = script.filename || "script";
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Download failed");
        }
    };

    const submitComment = async (e) => {
        e.preventDefault();
        if (!draft.trim()) return;
        setPosting(true);
        try {
            await axios.post(
                `${backend}/api/client/scripts/${script.id}/comments`,
                { body: draft.trim() },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            setDraft("");
            loadComments();
            toast.success("Feedback sent");
        } catch {
            toast.error("Could not post feedback");
        } finally {
            setPosting(false);
        }
    };

    return (
        <li
            data-testid={`script-${script.id}`}
            className="border border-white/10 bg-ink-800/40"
        >
            <div className="flex items-start justify-between gap-3 p-5 border-b border-white/10">
                <div className="min-w-0">
                    <h3 className="font-display text-bone text-lg tracking-tight">
                        {script.label}
                    </h3>
                    {script.description && (
                        <p className="text-bone-300 text-sm mt-1">
                            {script.description}
                        </p>
                    )}
                    <p className="text-[10px] uppercase tracking-[0.2em] text-bone-500 mt-2">
                        {script.filename} ·{" "}
                        {(script.size / 1024).toFixed(0)} KB · sent{" "}
                        {new Date(script.created_at).toLocaleDateString()}
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {isPdf && (
                        <button
                            onClick={() => setShowViewer((s) => !s)}
                            data-testid={`script-toggle-${script.id}`}
                            className="text-xs uppercase tracking-[0.18em] text-bone-300 hover:text-ember border border-white/15 px-3 h-9 transition-colors"
                        >
                            {showViewer ? "Hide" : "Open"} preview
                        </button>
                    )}
                    <button
                        onClick={downloadFile}
                        data-testid={`script-download-${script.id}`}
                        className="text-xs uppercase tracking-[0.18em] text-ember border border-ember/40 px-3 h-9 hover:bg-ember/10 transition-colors"
                    >
                        Download
                    </button>
                </div>
            </div>

            {isPdf && showViewer && fileUrl && (
                <iframe
                    src={fileUrl}
                    title={script.label}
                    data-testid={`script-iframe-${script.id}`}
                    className="w-full bg-white"
                    style={{ height: "70vh" }}
                />
            )}
            {isPdf && showViewer && !fileUrl && (
                <div className="p-12 text-center text-bone-500 text-sm flex items-center justify-center gap-3">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading preview…
                </div>
            )}
            {!isPdf && (
                <div className="px-5 py-6 text-bone-300 text-sm bg-ink-800/30">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-bone-500 mr-2">
                        Preview unavailable
                    </span>
                    Inline preview is only available for PDFs. Use the{" "}
                    <strong className="text-ember">Download</strong> button above to open this {(script.filename || "").split(".").pop()?.toUpperCase() || "file"}.
                </div>
            )}

            <div className="p-5 border-t border-white/10">
                <p className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-3">
                    Feedback on this script
                </p>
                <form
                    onSubmit={submitComment}
                    data-testid={`script-feedback-form-${script.id}`}
                    className="flex items-start gap-3 mb-4"
                >
                    <textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        rows={2}
                        placeholder="Share notes, edits, or questions about this script…"
                        data-testid={`script-feedback-input-${script.id}`}
                        className="flex-1 bg-transparent border border-white/10 focus:border-ember outline-none p-3 text-bone placeholder:text-bone-500 text-sm transition-colors resize-none"
                    />
                    <button
                        type="submit"
                        disabled={posting || !draft.trim()}
                        data-testid={`script-feedback-submit-${script.id}`}
                        className="btn-ember inline-flex items-center gap-2 h-10 px-4 text-xs uppercase tracking-[0.2em] disabled:opacity-50"
                    >
                        {posting ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                            <Send className="w-3.5 h-3.5" />
                        )}
                        Send
                    </button>
                </form>
                {comments.length > 0 && (
                    <ul className="space-y-3">
                        {comments
                            .slice()
                            .reverse()
                            .map((c) => (
                                <li
                                    key={c.id}
                                    className={`p-3 border ${
                                        c.author_type === "admin"
                                            ? "border-ember/30 bg-ember/5"
                                            : "border-white/10"
                                    }`}
                                >
                                    <div className="flex items-center justify-between text-xs mb-1">
                                        <span className="font-display text-bone tracking-tight">
                                            {c.author_name}
                                            {c.author_type === "admin" && (
                                                <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-ember">
                                                    Studio
                                                </span>
                                            )}
                                        </span>
                                        <span className="text-bone-500">
                                            {new Date(c.created_at).toLocaleString()}
                                        </span>
                                    </div>
                                    <p className="text-bone-300 text-sm whitespace-pre-wrap">
                                        {c.body}
                                    </p>
                                </li>
                            ))}
                    </ul>
                )}
            </div>
        </li>
    );
}
